import { HOT } from "@hot-wallet/sdk";
import WebApp from "@twa-dev/sdk";
import { t } from "i18next";
import { useSWRConfig } from "swr";
import useSWRImmutable from "swr/immutable";

import { ToastService } from "@/components/ToastService";
import { TOAST_ID } from "@/constants";
import { CHAIN_BY_NETWORK } from "@/constants/chains";
import { ColoredNetworkIconMap } from "@/constants/map";
import { API_ROUTES, SuccessResponse, TrustResponseData } from "@/interfaces/api";
import { MIXPANEL_EVENTS } from "@/interfaces/mixpanel";
import { Network } from "@/interfaces/network";
import { hapiApi } from "@/services/api/hapi";
import { logger } from "@/services/config/mixpanel";
import { colors } from "@/theme/colors";
import { ApiVersion, apiVersion } from "@/utils";

import { useStore } from "../store";

export const useTrustScore = () => {
  const config = useSWRConfig();
  const init = WebApp.initDataUnsafe;

  const { user, network, currentNetworkData, updateScore } = useStore((store) => ({
    user: store.user,
    network: store.network,
    currentNetworkData: store.user.networksData[store.network],
    updateScore: store.updateScore,
  }));
  const swrKey = `${API_ROUTES.TRUST}/${network}/${apiVersion === ApiVersion.v1 ? user.nearAccount : user.tonAccount}`;
  const toastID = `${TOAST_ID.TRUST_SCORE}/${network}`;
  const icon = ColoredNetworkIconMap[network];

  return useSWRImmutable<TrustResponseData | undefined>(
    swrKey,
    async () => {
      let address;
      switch (network) {
        case Network.NEAR: {
          address = user.nearAccount;
          break;
        }
        case Network.SOLANA: {
          address = user.solanaAccount;
          break;
        }
        case Network.TON:
        case Network.TON_TESTNET: {
          address = user.tonAccount;
          break;
        }
        case Network.STELLAR: {
          address = user.stellarAccount;
          break;
        }
        default: {
          address = user.evmAccount;
          break;
        }
      }
      if (!address && network === Network.STELLAR) return;
      if (!address) throw Error(t("Messages.CreateMPCWallet"));

      ToastService.loading({ text: t("Messages.LoadingTrust", { network }), id: toastID });
      const connection = await HOT.connection;
      const data = await hapiApi.getTrust({
        network,
        nearAddress: user.nearAccount,
        address: network === Network.NEAR ? undefined : address,
        chainId: CHAIN_BY_NETWORK[network],
        tg: connection?.telegramId || init.user?.id,
      });
      console.log("data trustscore", data);
      if (data.errorCode === 1) throw Error(data.message || t("Messages.SomethingWentWrong"));

      return data as SuccessResponse & TrustResponseData;
    },
    {
      onSuccess(data) {
        if (!data) return;
        updateScore(network, { ...data });

        const oldScore = currentNetworkData.score;
        const newScore = data.trust;
        logger.trackEvent(MIXPANEL_EVENTS.TRUST_SCORE, { new: newScore, old: oldScore });
        if (newScore > oldScore.value) {
          ToastService.update({
            id: toastID,
            text: t("Messages.HigherTrustScore"),
            icon,
            style: { background: colors.green, color: colors.white, border: "none" },
          });
        } else {
          if (newScore === 0) {
            ToastService.update({ id: toastID, text: t("Messages.ZeroNewTrustScore"), icon });
          } else {
            ToastService.update({
              id: toastID,
              text: t("Messages.SameTrustScore"),
              icon,
              style: { background: colors.warning, color: colors.white, border: "none" },
            });
          }
        }
      },
      onError(err) {
        ToastService.update({ id: toastID, text: err.message, icon });
        if (err.message !== t("Messages.CreateMPCWallet")) {
          hapiApi.logError({ nearAddress: user.nearAccount, action: "TRUST", error: err });
        }
        console.error("Error, while recheck score from api \n\n", err);
      },
      revalidateOnMount: !config.cache.get(swrKey),
    },
  );
};
